// In-app log buffer — a capped ring of recent log lines, surfaced through the
// version badge's modal so a tester can COPY the logs and hand them over
// (Brief 5 finding, 2026-07-10: diagnosing the sign-in handoff needed the
// device's own logs, which are otherwise invisible on a remote Windows box).
//
// It mirrors `console.*` into the ring (so nothing already logged is lost) and
// exposes `logEvent` for deliberate, structured breadcrumbs (e.g. each deep
// link received). Pure module state; no framework deps.

export interface LogLine {
  t: number; // epoch ms
  level: "log" | "info" | "warn" | "error" | "event";
  msg: string;
}

const MAX_LINES = 500;
const lines: LogLine[] = [];
const listeners = new Set<() => void>();

function push(level: LogLine["level"], msg: string): void {
  lines.push({ t: Date.now(), level, msg });
  if (lines.length > MAX_LINES) lines.splice(0, lines.length - MAX_LINES);
  listeners.forEach((fn) => fn());
}

/** Best-effort stringify of a console argument (objects → JSON, else String). */
function fmt(arg: unknown): string {
  if (typeof arg === "string") return arg;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}

/** A deliberate, structured breadcrumb (survives even if console is quiet). */
export function logEvent(msg: string): void {
  push("event", msg);
}

/** Current buffer snapshot (oldest first). */
export function getLogLines(): LogLine[] {
  return lines.slice();
}

/** The whole buffer as copy-pasteable text. */
export function logsAsText(): string {
  return lines
    .map((l) => {
      const ts = new Date(l.t).toISOString();
      return `${ts} [${l.level}] ${l.msg}`;
    })
    .join("\n");
}

/** Subscribe to buffer changes (the modal re-renders on each). Returns an
 * unsubscribe. */
export function subscribeLogs(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

let installed = false;
/** Mirror console.{log,info,warn,error} into the ring — once, idempotent. Each
 * original method still runs, so devtools behaviour is unchanged. */
export function installConsoleCapture(): void {
  if (installed) return;
  installed = true;
  (["log", "info", "warn", "error"] as const).forEach((level) => {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      push(level, args.map(fmt).join(" "));
      original(...args);
    };
  });
}
